import { supabase } from '@/integrations/supabase/client';
import {
  CareersPageSettings,
  Job,
  JobApplication,
  JobApplicationInput,
  JobApplicationStatus,
  JobInput,
  toJobSlug,
} from '@/types/career';

type JobRow = {
  id: string;
  slug: string;
  title_en: string;
  title_fr: string;
  short_description_en: string;
  short_description_fr: string;
  description_en: string;
  description_fr: string;
  missions_fr?: string | null;
  profile_fr?: string | null;
  offer_fr?: string | null;
  location: string;
  map_lat?: number | null;
  map_lng?: number | null;
  employment_type: string;
  department: string | null;
  cover_image: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
};

type ApplicationRow = {
  id: string;
  job_id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  cv_url: string;
  extra_info: string | null;
  status: string;
  created_at: string;
  jobs?: {
    id: string;
    slug: string;
    title_en: string;
    title_fr: string;
  } | null;
};

type CareersSettingsRow = {
  id: string;
  hero_eyebrow_en: string | null;
  hero_eyebrow_fr: string | null;
  hero_title_en: string | null;
  hero_title_fr: string | null;
  hero_subtitle_en: string | null;
  hero_subtitle_fr: string | null;
  hero_background_image: string | null;
};

const mapJob = (row: JobRow): Job => ({
  id: row.id,
  slug: row.slug,
  title: { en: row.title_en || row.title_fr, fr: row.title_fr },
  shortDescription: {
    en: row.short_description_en || row.short_description_fr,
    fr: row.short_description_fr,
  },
  description: {
    en: row.description_en || row.description_fr,
    fr: row.description_fr,
  },
  missions: row.missions_fr || '',
  profile: row.profile_fr || '',
  offer: row.offer_fr || '',
  location: row.location,
  mapLat: row.map_lat ?? undefined,
  mapLng: row.map_lng ?? undefined,
  employmentType: row.employment_type,
  department: row.department || undefined,
  coverImage: row.cover_image || undefined,
  isPublished: row.is_published,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapApplication = (row: ApplicationRow): JobApplication => ({
  id: row.id,
  jobId: row.job_id,
  firstName: row.first_name,
  lastName: row.last_name,
  phone: row.phone,
  email: row.email,
  cvUrl: row.cv_url,
  extraInfo: row.extra_info || undefined,
  status: (row.status as JobApplicationStatus) || 'new',
  createdAt: row.created_at,
  job: row.jobs
    ? {
        id: row.jobs.id,
        slug: row.jobs.slug,
        title: { en: row.jobs.title_en, fr: row.jobs.title_fr },
      }
    : undefined,
});

const mapSettings = (row: CareersSettingsRow): CareersPageSettings => ({
  id: row.id,
  heroEyebrowEn: row.hero_eyebrow_en || undefined,
  heroEyebrowFr: row.hero_eyebrow_fr || undefined,
  heroTitleEn: row.hero_title_en || undefined,
  heroTitleFr: row.hero_title_fr || undefined,
  heroSubtitleEn: row.hero_subtitle_en || undefined,
  heroSubtitleFr: row.hero_subtitle_fr || undefined,
  heroBackgroundImage: row.hero_background_image || undefined,
});

const toJobRow = (input: JobInput) => {
  const title = input.title.trim();
  const shortDescription = (input.shortDescription || '').trim();
  const description = input.description.trim();
  return {
    slug: toJobSlug(title) || `job-${Date.now()}`,
    title_en: title,
    title_fr: title,
    short_description_en: shortDescription,
    short_description_fr: shortDescription,
    description_en: description,
    description_fr: description,
    missions_fr: input.missions.trim(),
    profile_fr: input.profile.trim(),
    offer_fr: input.offer.trim(),
    location: input.location.trim(),
    map_lat: Number.isFinite(input.mapLat) ? input.mapLat : null,
    map_lng: Number.isFinite(input.mapLng) ? input.mapLng : null,
    employment_type: input.employmentType.trim(),
    department: input.department || null,
    cover_image: input.coverImage || null,
    is_published: input.isPublished,
  };
};

export const fetchPublishedJobs = async (): Promise<Job[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('is_published', true)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as JobRow[] | null)?.map(mapJob) || [];
};

export const fetchPublishedJobBySlug = async (slug: string): Promise<Job | null> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('slug', slug)
    .eq('is_published', true)
    .maybeSingle();

  if (error) throw error;
  return data ? mapJob(data as JobRow) : null;
};

export const fetchAdminJobs = async (): Promise<Job[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as JobRow[] | null)?.map(mapJob) || [];
};

export const fetchAdminJobById = async (id: string): Promise<Job> => {
  const { data, error } = await supabase.from('jobs').select('*').eq('id', id).single();
  if (error) throw error;
  return mapJob(data as JobRow);
};

export const createJob = async (input: JobInput): Promise<Job> => {
  const { data, error } = await supabase.from('jobs').insert([toJobRow(input)]).select('*').single();
  if (error) throw error;
  return mapJob(data as JobRow);
};

export const updateJob = async (id: string, input: JobInput): Promise<Job> => {
  const payload = toJobRow(input);
  // Keep existing slug on update so public links stay stable
  const { data: current } = await supabase.from('jobs').select('slug').eq('id', id).maybeSingle();
  if (current?.slug) payload.slug = current.slug;

  const { data, error } = await supabase
    .from('jobs')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapJob(data as JobRow);
};

export const deleteJob = async (id: string): Promise<void> => {
  const { error } = await supabase.from('jobs').delete().eq('id', id);
  if (error) throw error;
};

export const uploadCareerImage = async (file: File): Promise<string> => {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `jobs/job-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('careers').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from('careers').getPublicUrl(path);
  return data.publicUrl;
};

export const uploadCareerCv = async (file: File): Promise<string> => {
  const ext = file.name.split('.').pop() || 'pdf';
  const path = `cvs/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('career-cvs').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'application/pdf',
  });
  if (error) throw error;
  // Private bucket — store the storage path; admins fetch via signed URL
  return path;
};

export const getCareerCvSignedUrl = async (pathOrUrl: string): Promise<string> => {
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  const { data, error } = await supabase.storage
    .from('career-cvs')
    .createSignedUrl(pathOrUrl, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
};

const guessCvExtension = (pathOrUrl: string, mime?: string): string => {
  const fromPath = pathOrUrl.split('?')[0].split('.').pop()?.toLowerCase();
  if (fromPath && fromPath.length <= 5) return fromPath;
  if (mime?.includes('pdf')) return 'pdf';
  if (mime?.includes('word')) return 'docx';
  if (mime?.includes('jpeg')) return 'jpg';
  if (mime?.includes('png')) return 'png';
  return 'pdf';
};

/** Download CV blob and trigger a browser file save (PDF when the file is a PDF). */
export const downloadCareerCv = async (
  pathOrUrl: string,
  fileNameBase = 'CV'
): Promise<void> => {
  let blob: Blob;
  let mime = '';

  if (pathOrUrl.startsWith('http')) {
    const response = await fetch(pathOrUrl);
    if (!response.ok) throw new Error('Failed to fetch CV');
    blob = await response.blob();
    mime = blob.type || response.headers.get('content-type') || '';
  } else {
    const { data, error } = await supabase.storage.from('career-cvs').download(pathOrUrl);
    if (error || !data) throw error || new Error('Failed to download CV');
    blob = data;
    mime = data.type || '';
  }

  const ext = guessCvExtension(pathOrUrl, mime);
  const isPdf = mime.includes('pdf') || ext === 'pdf';
  const safeBase =
    fileNameBase
      .replace(/[^\w\-À-ÿ ]+/gi, '')
      .trim()
      .replace(/\s+/g, '_') || 'CV';
  const fileName = isPdf ? `${safeBase}.pdf` : `${safeBase}.${ext}`;
  const downloadBlob = isPdf ? new Blob([blob], { type: 'application/pdf' }) : blob;

  const objectUrl = URL.createObjectURL(downloadBlob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const submitJobApplication = async (
  input: JobApplicationInput
): Promise<JobApplication> => {
  const { data, error } = await supabase
    .from('job_applications')
    .insert([
      {
        job_id: input.jobId,
        first_name: input.firstName,
        last_name: input.lastName,
        phone: input.phone,
        email: input.email,
        cv_url: input.cvUrl,
        extra_info: input.extraInfo || null,
        status: 'new',
      },
    ])
    .select('*')
    .single();

  if (error) throw error;
  return mapApplication(data as ApplicationRow);
};

export const fetchJobApplications = async (jobId?: string): Promise<JobApplication[]> => {
  let query = supabase
    .from('job_applications')
    .select('*, jobs(id, slug, title_en, title_fr)')
    .order('created_at', { ascending: false });

  if (jobId) query = query.eq('job_id', jobId);

  const { data, error } = await query;
  if (error) throw error;
  return (data as ApplicationRow[] | null)?.map(mapApplication) || [];
};

export const updateJobApplicationStatus = async (
  id: string,
  status: JobApplicationStatus
): Promise<JobApplication> => {
  const { data, error } = await supabase
    .from('job_applications')
    .update({ status })
    .eq('id', id)
    .select('*, jobs(id, slug, title_en, title_fr)')
    .single();
  if (error) throw error;
  return mapApplication(data as ApplicationRow);
};

export const fetchCareersPageSettings = async (): Promise<CareersPageSettings> => {
  const { data, error } = await supabase
    .from('careers_page_settings')
    .select('*')
    .eq('id', 'careers-page')
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    return {
      id: 'careers-page',
      heroEyebrowEn: 'Join Meubles Karim',
      heroEyebrowFr: 'Rejoignez Meubles Karim',
      heroTitleEn: 'Careers',
      heroTitleFr: 'Carrières',
      heroSubtitleEn:
        'Explore open roles and grow with a team passionate about design and craftsmanship.',
      heroSubtitleFr:
        'Découvrez nos offres et évoluez avec une équipe passionnée par le design et le savoir-faire.',
    };
  }
  return mapSettings(data as CareersSettingsRow);
};

export const saveCareersPageSettings = async (
  settings: Partial<CareersPageSettings>
): Promise<CareersPageSettings> => {
  const payload = {
    id: 'careers-page',
    hero_eyebrow_en: settings.heroEyebrowEn ?? null,
    hero_eyebrow_fr: settings.heroEyebrowFr ?? null,
    hero_title_en: settings.heroTitleEn ?? null,
    hero_title_fr: settings.heroTitleFr ?? null,
    hero_subtitle_en: settings.heroSubtitleEn ?? null,
    hero_subtitle_fr: settings.heroSubtitleFr ?? null,
    hero_background_image: settings.heroBackgroundImage ?? null,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('careers_page_settings')
    .upsert(payload)
    .select('*')
    .single();

  if (error) throw error;
  return mapSettings(data as CareersSettingsRow);
};
